import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail, fmtMoney, fmtCliente } from "@/lib/api";
import { SectionCard } from "./SectionCard";
import { ImportBtn } from "./CuentasDetalle";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Check, X, Scale, ChevronDown, ChevronRight, Trash2, Loader2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";

const money = (n) => fmtMoney(n);

function UtilBar({ pct }) {
  const reduced = useReducedMotion();
  if (pct === null || pct === undefined)
    return (
      <div className="flex items-center justify-center" data-testid="util-na">
        <span className="text-xs text-slate-400">—</span>
      </div>
    );
  // Verde <70%, ámbar 70–100%, rojo >100%
  const color = pct > 100 ? "#dc2626" : pct >= 70 ? "#d97706" : "#16a34a";
  const w = Math.max(0, Math.min(pct, 100));
  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="util-track h-2 w-10 sm:w-14">
        <motion.div
          className="util-bar"
          style={{ background: color }}
          initial={reduced ? false : { width: 0 }}
          animate={{ width: `${w}%` }}
          transition={reduced ? { duration: 0 } : { duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <span className="font-mono text-[11px] font-semibold w-9 text-right tabular-nums" style={{ color }}>
        {pct}%
      </span>
    </div>
  );
}

function CuentasSkeleton() {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5" data-testid="cuentas-skeleton">
      <div className="h-4 w-2/5 rounded bg-slate-200 animate-pulse mb-4" />
      <div className="space-y-2.5">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-3.5 flex-1 rounded bg-slate-100 animate-pulse" />
            <div className="h-3.5 w-20 rounded bg-slate-100 animate-pulse" />
            <div className="h-3.5 w-20 rounded bg-slate-100 animate-pulse" />
            <div className="h-3.5 w-24 rounded bg-slate-200 animate-pulse" />
            <div className="h-2 w-14 rounded-full bg-slate-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Cuentas({ data, canEdit = false, canDelete = false, onSetLinea, onRefresh, density = "compact", vendedores = [], focusCliente = null, onFocusDone }) {
  const [editing, setEditing] = useState(null);
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    if (!focusCliente) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-cliente-row="${CSS.escape(focusCliente)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlight(focusCliente);
        setTimeout(() => setHighlight(null), 2600);
      }
      onFocusDone?.();
    }, 250);
    return () => clearTimeout(t);
  }, [focusCliente, data]);
  const [valC, setValC] = useState("");
  const [valA, setValA] = useState("");
  const [jud, setJud] = useState(null);
  const [openJud, setOpenJud] = useState({});
  const [judVend, setJudVend] = useState("__all__");
  const [judDeleting, setJudDeleting] = useState(false);
  const toggleJud = (cli) => setOpenJud((o) => ({ ...o, [cli]: !o[cli] }));

  const loadJud = useCallback(async () => {
    try {
      const res = await api.get("/cuentas-judicial");
      setJud(res.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadJud(); }, [loadJud, data]);

  const borrarJudiciales = async () => {
    const porVend = judVend !== "__all__";
    const msg = porVend
      ? `¿Borrar TODOS los casos judiciales del vendedor «${judVend}»? Esta acción no se puede deshacer.`
      : "¿Borrar TODOS los casos judiciales (de todos los vendedores)? Esta acción no se puede deshacer.";
    if (!window.confirm(msg)) return;
    setJudDeleting(true);
    const tid = toast.loading("Borrando casos judiciales…");
    try {
      const res = await api.delete("/cuentas-judicial", { params: porVend ? { vendedor: judVend } : {} });
      toast.success(res.data?.message || "Casos judiciales eliminados", { id: tid });
      setJudVend("__all__");
      await loadJud();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo borrar", { id: tid });
    } finally {
      setJudDeleting(false);
    }
  };

  if (!data) return <CuentasSkeleton />;
  const t = data.totales;
  const anyLine = data.items.some((r) => r.saldo_disponible !== null && r.saldo_disponible !== undefined);
  const saldoDispTot = anyLine ? data.items.reduce((s, r) => s + (r.saldo_disponible ?? 0), 0) : null;
  const dispNetoTot = anyLine ? data.items.reduce((s, r) => s + (r.disponible_neto ?? 0), 0) : null;

  const ample = density === "ample";
  const numCell = ample
    ? "px-4 py-3 text-right text-sm whitespace-nowrap tabular-nums font-medium"
    : "px-4 py-2.5 text-right text-[13px] whitespace-nowrap tabular-nums font-medium";
  const numHead = ample
    ? "px-4 py-3 text-right font-semibold whitespace-nowrap"
    : "px-4 py-2.5 text-right font-semibold whitespace-nowrap";
  const headTr = ample
    ? "bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide"
    : "bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide";
  const nameCell = ample
    ? "px-4 py-3 font-medium text-slate-800 text-sm sticky left-0 bg-white z-[1] max-w-[220px] truncate"
    : "px-4 py-2.5 font-medium text-slate-800 text-[13px] sticky left-0 bg-white z-[1] max-w-[200px] truncate";

  const startEdit = (r) => { setEditing(r.cliente); setValC(String(r.linea_credito || 0)); setValA(String(r.adicionales || 0)); };
  const save = async (cliente) => {
    const c = parseFloat(valC) || 0;
    const a = parseFloat(valA) || 0;
    if (onSetLinea) await onSetLinea(cliente, c, a);
    setEditing(null);
  };

  return (
    <>
    <SectionCard
      id="cuentas"
      testid="section-cuentas"
      num="2"
      title="Cuentas por Recibir, Líneas Totales y % de Utilización"
      description="Resumen consolidado (excluye casos judiciales). Cifras en dólares (USD). Fact. + Pend. = facturado + pedidos pendientes."
      action={canEdit ? (
        <ImportBtn onDone={() => { onRefresh?.(); }} label="Cargar PDF detallado" testid="cuentas-sec2" vendedores={vendedores} requireVendedor />
      ) : null}
    >
      <div className="overflow-auto max-h-[72vh] rounded-lg border border-slate-100 pb-20">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-[2]">
            <tr className={headTr}>
              <th className="text-left px-4 py-2.5 font-semibold min-w-[170px] w-[19%] sticky left-0 bg-slate-50 z-[3]">Cliente</th>
              <th className={numHead}>Vencido (USD)</th>
              <th className={numHead}>A Vencer (USD)</th>
              <th className={numHead}>Total Cartera (USD)</th>
              <th className={numHead}>Línea Total (USD)</th>
              <th className={numHead}>% Línea Fact.</th>
              <th className={numHead}>Fact. + Pend. (USD)</th>
              <th className={numHead}>Línea Disp. vs Fact. (USD)</th>
              <th className={numHead}>Línea Disp. vs Fact. + Pend. (USD)</th>
              <th className="text-right px-2 py-2.5 font-semibold whitespace-nowrap min-w-[92px]">% Fact. + Pend.</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, i) => (
              <tr key={i} data-cliente-row={r.cliente}
                className={`border-b border-[#eee] transition-colors ${highlight === r.cliente ? "bg-amber-100 ring-2 ring-amber-400" : "hover:bg-emerald-50/40"}`}>
                <td className={`${nameCell} ${highlight === r.cliente ? "bg-amber-100" : ""}`}>
                  <span className="block truncate" title={r.cliente} style={{ maxWidth: "280px" }}>{fmtCliente(r.cliente)}</span>
                </td>
                <td className={`${numCell} text-red-600`}>{money(r.valor_vencido)}</td>
                <td className={`${numCell} text-slate-700`}>{money(r.valor_a_vencer)}</td>
                <td className={`${numCell} font-semibold text-slate-900`}>{money(r.total_cartera)}</td>
                <td className={`${numCell} text-slate-700`}>
                  {canEdit && editing === r.cliente ? (
                    <span className="inline-flex items-center gap-1 justify-end">
                      <input
                        autoFocus type="number" value={valC}
                        data-testid="linea-credito-input" title="Línea de Crédito"
                        onChange={(e) => setValC(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(r.cliente); if (e.key === "Escape") setEditing(null); }}
                        className="w-20 h-7 border border-emerald-300 rounded px-1.5 text-right text-[13px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                      <span className="text-slate-400">+</span>
                      <input
                        type="number" value={valA}
                        data-testid="linea-adicional-input" title="Adicionales"
                        onChange={(e) => setValA(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(r.cliente); if (e.key === "Escape") setEditing(null); }}
                        className="w-16 h-7 border border-emerald-300 rounded px-1.5 text-right text-[13px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                      <button data-testid="linea-save" onClick={() => save(r.cliente)} className="text-emerald-700 hover:text-emerald-900"><Check className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                    </span>
                  ) : canEdit ? (
                    <button data-testid="linea-edit" onClick={() => startEdit(r)}
                      className="group inline-flex items-center gap-1 justify-end hover:text-emerald-700"
                      title="Editar Línea (Crédito + Adicionales)">
                      {money(r.linea_total)}
                      <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ) : money(r.linea_total)}
                </td>
                <td className={`${numCell} text-slate-600`}>
                  {r.pct_linea_facturado === null || r.pct_linea_facturado === undefined ? "N/A" : `${r.pct_linea_facturado}%`}
                </td>
                <td className={`${numCell} font-semibold text-emerald-700`}>{money(r.valor_linea_usd)}</td>
                <td className={`${numCell} ${r.saldo_disponible < 0 ? "text-red-600" : "text-slate-700"}`}>
                  {r.saldo_disponible === null || r.saldo_disponible === undefined ? "N/A" : money(r.saldo_disponible)}
                </td>
                <td className={`${numCell} ${r.disponible_neto < 0 ? "text-red-600" : "text-slate-700"}`}>
                  {r.disponible_neto === null || r.disponible_neto === undefined ? "N/A" : money(r.disponible_neto)}
                </td>
                <td className="px-2.5 py-2"><UtilBar pct={r.pct_utilizacion} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-emerald-50 border-t-2 border-emerald-200 font-semibold text-[13px]">
              <td className="px-3 py-2.5 text-emerald-900 uppercase text-[11px] tracking-wide">Totales</td>
              <td className={`${numCell} text-red-700`}>{money(t.valor_vencido)}</td>
              <td className={`${numCell} text-slate-800`}>{money(t.valor_a_vencer)}</td>
              <td className={`${numCell} text-emerald-900`}>{money(t.total_cartera)}</td>
              <td className={`${numCell} text-slate-800`}>{money(t.linea_total)}</td>
              <td className="px-2.5 py-2.5"></td>
              <td className={`${numCell} text-emerald-900`}>{money(t.valor_linea_usd)}</td>
              <td className={`${numCell} text-slate-800`}>{saldoDispTot === null ? "N/A" : money(saldoDispTot)}</td>
              <td className={`${numCell} text-slate-800`}>{dispNetoTot === null ? "N/A" : money(dispNetoTot)}</td>
              <td className="px-2.5 py-2.5"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </SectionCard>

    {jud && jud.clientes > 0 && (
      <SectionCard
        id="judiciales"
        testid="section-judiciales"
        num="J"
        title="Casos Judiciales"
        description={jud.fuente === "listado"
          ? "Valores tomados de la planilla cargada en Casos judiciales (saldo corregido). Agrupados por cliente."
          : "Facturas con observación JUDICIAL (excluidas de la Sección 2). Agrupadas por cliente."}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <Scale className="h-4 w-4 text-red-600" />
          <span data-testid="judicial-resumen"><strong>{jud.clientes}</strong> cliente(s) · Total <strong className="text-red-700">{money(jud.total)}</strong></span>
          {jud.fuente === "listado" && jud.agregado_total > 0 && (
            <span className="text-[11px] text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-2 py-0.5" data-testid="judicial-agregado">
              incluye {money(jud.agregado_total)} que no estaban como judicial en la cartera (tomado de la planilla)
            </span>
          )}
          {canDelete && jud.fuente !== "listado" && (
            <div className="flex items-center gap-2 ml-auto">
              <Select value={judVend} onValueChange={setJudVend}>
                <SelectTrigger className="h-9 w-44" data-testid="judicial-vendedor-filter">
                  <SelectValue placeholder="Vendedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los vendedores</SelectItem>
                  {(jud.vendedores || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={borrarJudiciales} disabled={judDeleting}
                data-testid="judicial-delete-btn"
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                {judDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {judVend === "__all__" ? "Borrar todos" : "Borrar de este vendedor"}
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {jud.items.map((c) => {
            const open = !!openJud[c.cliente];
            return (
            <div key={c.cliente} className="rounded-lg border border-red-100 overflow-hidden" data-testid="judicial-cliente">
              <button
                onClick={() => toggleJud(c.cliente)}
                data-testid="judicial-toggle"
                className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 bg-red-50 hover:bg-red-100 transition-colors text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {open ? <ChevronDown className="h-4 w-4 text-red-600 shrink-0" /> : <ChevronRight className="h-4 w-4 text-red-600 shrink-0" />}
                  <span className="font-semibold text-red-900 text-sm truncate">{c.cliente}{c.codigo ? ` · ${c.codigo}` : ""}</span>
                  <span className="text-[11px] text-red-500 shrink-0">({c.vencidos.length} venc.)</span>
                  {c.agregado_listado > 0 && <span className="text-[10px] text-amber-700 shrink-0">+{money(c.agregado_listado)} desde la planilla</span>}
                </div>
                <span className="font-mono text-sm text-red-800 tabular-nums shrink-0">{money(c.total)}</span>
              </button>
              {open && (
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-slate-400 text-[10px] uppercase tracking-wide">
                    <th className="text-left px-3 py-1.5">Factura</th>
                    <th className="text-center px-3 py-1.5">Vencimiento</th>
                    <th className="text-center px-3 py-1.5">Estado</th>
                    <th className="text-right px-3 py-1.5">Saldo (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {c.vencidos.map((v, i) => (
                    <tr key={i} className="border-t border-slate-50">
                      <td className="px-3 py-1.5 text-slate-700">{v.factura || "—"}</td>
                      <td className="px-3 py-1.5 text-center text-slate-600">{v.vencimiento || "—"}</td>
                      <td className={`px-3 py-1.5 text-center font-medium ${v.vencido ? "text-red-600" : "text-slate-500"}`}>{v.vencido ? "Vencida" : "A vencer"}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{money(v.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          );})}
        </div>
        {jud.fuente === "listado" && jud.fuera_del_listado?.length > 0 && (
          <p className="mt-3 text-[11px] text-amber-700" data-testid="judicial-fuera">
            Con facturas marcadas JUDICIAL en la cartera pero que no figuran en la planilla (no se suman acá): {jud.fuera_del_listado.map((f) => `${f.cliente} (${money(f.monto)})`).join(" · ")}. Revisalos en Casos judiciales.
          </p>
        )}
      </SectionCard>
    )}
    </>
  );
}
